import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import "../pages/Management.css"; // Ensure this file exists

function Management() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStaff, setEditedStaff] = useState(null);
  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Update fieldOrder to use 'role' (singular)
  const fieldOrder = [
    "name",
    "nickname",
    "streetAddress",
    "city",
    "zip",
    "phone",
    "email",
    "hireDate",
    "role",
    "availability",
    "notes"
  ];

  // Role options matching addstaff.js
  const roleOptions = ["Any job", "Small Lead", "2nd Shooter", "Office"];

  // Fetch staff members from Firestore
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "staff"));
        const staffList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setStaff(staffList);
      } catch (error) {
        console.error("Error fetching staff:", error);
      }
    };

    fetchStaff();
  }, []);

  // Update filteredStaff when staff or searchTerm changes
  useEffect(() => {
    const filtered = staff.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStaff(filtered);
  }, [searchTerm, staff]);

  // Handle selecting a staff member
  const handleStaffClick = (staffMember) => {
    setSelectedStaff(staffMember);
    setIsEditing(false);
  };

  // Handle enabling edit mode
  const handleEditClick = () => {
    setEditedStaff({ ...selectedStaff });
    setIsEditing(true);
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    if (!editedStaff) return;

    try {
      const staffRef = doc(db, "staff", editedStaff.id);
      await updateDoc(staffRef, {
        ...editedStaff,
        availability: Array.isArray(editedStaff.availability) ? editedStaff.availability : [],
      });

      // Update UI
      setSelectedStaff(editedStaff);
      setIsEditing(false);

      // Update staff list
      setStaff(prevStaff =>
        prevStaff.map(staff =>
          staff.id === editedStaff.id ? { ...editedStaff } : staff
        )
      );

      alert("Staff details updated successfully!");
    } catch (error) {
      console.error("Error updating staff:", error);
      alert("Failed to update staff.");
    }
  };

  // Handle Availability Checkbox Selection
  const handleAvailabilityChange = (day) => {
    setEditedStaff((prev) => ({
      ...prev,
      availability: prev.availability.includes(day)
        ? prev.availability.filter((d) => d !== day)
        : [...prev.availability, day],
    }));
  };

  // Handle delete staff
  const handleDeleteStaff = async (staffId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this staff member?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "staff", staffId));
      // Remove from local state so UI refreshes
      setStaff(prevStaff => prevStaff.filter(member => member.id !== staffId));
      // Clear the selected staff details panel
      setSelectedStaff(null);
      setIsEditing(false);
      alert("Staff member deleted successfully!");
    } catch (error) {
      console.error("Error deleting staff:", error);
      alert("Failed to delete staff.");
    }
  };

  return (
    <div className="management-container">
      {/* Left Panel - Staff List */}
      <div className="left-panel">
        <h2>Staff Management</h2>
        <input
          type="text"
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {/* Add Staff Button */}
        <Link to="/addstaff">
          <button className="export-button">Add Staff</button>
        </Link>
        {/* Staff List */}
        <ul className="staff-list">
          {filteredStaff.map(member => (
            <li key={member.id} className="staff-item" onClick={() => handleStaffClick(member)}>
              {member.name}
            </li>
          ))}
        </ul>
      </div>

      {/* Right Panel - Staff Details */}
      <div className="right-panel">
        {selectedStaff ? (
          <div className="staff-details">
            {isEditing ? (
              <>
                <h2>Edit Staff</h2>
                {fieldOrder.map((field) => {
                  if (field === "availability") {
                    // Render checkboxes only for availability
                    return (
                      <div key={field} className="field-container">
                        <label>Availability:</label>
                        <div className="availability-checkboxes">
                          {daysOfWeek.map((day) => (
                            <label key={day} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={editedStaff?.availability?.includes(day) || false}
                                onChange={() => handleAvailabilityChange(day)}
                              />
                              {day}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  } else if (field === "role") {
                    // Render dropdown for role matching addstaff.js
                    return (
                      <div key={field} className="field-container">
                        <label>{field.replace(/([A-Z])/g, ' $1').trim()}:</label>
                        <select
                          value={editedStaff.role || ""}
                          onChange={(e) =>
                            setEditedStaff({
                              ...editedStaff,
                              role: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Role</option>
                          {roleOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    // Render text input for other fields
                    return (
                      <div key={field} className="field-container">
                        <label>{field.replace(/([A-Z])/g, ' $1').trim()}:</label>
                        <input
                          type="text"
                          value={editedStaff[field] || ""}
                          onChange={(e) =>
                            setEditedStaff({
                              ...editedStaff,
                              [field]: e.target.value,
                            })
                          }
                        />
                      </div>
                    );
                  }
                })}
                <button onClick={handleSaveChanges}>Save</button>
                <button onClick={() => setIsEditing(false)}>Cancel</button>
                <button
                  style={{ backgroundColor: "red", color: "#fff" }}
                  onClick={() => handleDeleteStaff(selectedStaff.id)}
                >
                  Delete Staff
                </button>
              </>
            ) : (
              <>
                <h2>{selectedStaff.name}</h2>
                {fieldOrder.map((field) => (
                  selectedStaff[field] !== undefined && (
                    <div key={field} className="field-container">
                      <h3>{field.replace(/([A-Z])/g, ' $1').trim()}:</h3>
                      <p>
                        {selectedStaff[field]?.seconds 
                          ? new Date(selectedStaff[field].seconds * 1000).toLocaleDateString()
                          : Array.isArray(selectedStaff[field]) 
                            ? selectedStaff[field].join(", ")
                            : typeof selectedStaff[field] === "object"
                              ? JSON.stringify(selectedStaff[field])
                              : selectedStaff[field]
                        }
                      </p>
                    </div>
                  )
                ))}
                <button onClick={handleEditClick} className="edit-button">Edit</button>
                <button
                  style={{ backgroundColor: "red", color: "#fff" }}
                  onClick={() => handleDeleteStaff(selectedStaff.id)}
                >
                  Delete Staff
                </button>
              </>
            )}
          </div>
        ) : (
          <h2>Select a staff member to view details</h2>
        )}
      </div>
    </div>
  );
}

export default Management;
